import React, { useEffect, useState } from "react";
import AppLayout from "../componets/AppLayout/AppLayout";
import {
  Autocomplete,
  Box,
  IconButton,
  Stack,
  TextField,
  useMediaQuery,
} from "@mui/material";
import GridCustomHeader from "../componets/AppLayout/GridCustomHeader";
import AddReferralModal from "../componets/Modal/AddReferralModal";
import { useNavigate } from "react-router-dom";
import { ReferralsAPI } from "../api/referral";
import VisibilityIcon from "../assets/SVGs/Login/VisibilityIcon";
import { DataGrid } from "@mui/x-data-grid";
import { filterArrayByString } from "../componets/helper";
import { defaultEditFormState } from "../componets/constant";
import EditReferralModal from "../componets/Modal/EditReferralModal";
import dayjs from "dayjs";
import { useAppContext } from "../context/AppContext";
import { DatePicker } from "@mui/x-date-pickers";
import { MembershipAPI } from "../api/membership";
import {
  memberOptionGroup,
  memberOptionLabel,
  sortByName,
  sortMembersDepartedLast,
} from "../componets/helper";
import renderMemberOption from "../componets/MemberOption";
import MemberNameCell from "../componets/MemberNameCell";

const Referral = () => {
  const { openSnack } = useAppContext();
  const navigate = useNavigate();
  const isMobileTable = useMediaQuery("(max-width:850px)");
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState([]);
  const [openEditModal, setOpenEditModal] = useState(defaultEditFormState);
  const [members, setMembers] = useState([]);

  const handleSearch = (searchString) => {
    setSearchText(searchString);
  };

  const fetchMembers = async () => {
    try {
      const response = await MembershipAPI.getMembers("");
      setMembers(sortByName(response?.items || [], "first_name"));
      return;
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    }
  };

  const columns = [
    {
      field: "referral_sender",
      headerName: "Referral Sender",
      flex: 1,
      minWidth: isMobileTable ? 180 : undefined,
      valueGetter: (params) => `${params?.first_name} ${params?.last_name}`,
      renderCell: (params) => (
        <MemberNameCell member={params?.row?.referral_sender} />
      ),
      editable: true,
      renderEditCell: (params) => {
        const handleChange = (_e, e) => {
          const newValue = e;
          params.api.setEditCellValue({
            id: params.id,
            field: "referral_sender",
            value: newValue,
          });
        };
        return (
          <Autocomplete
            value={params?.value || null}
            onChange={handleChange}
            id="referral_sender"
            options={sortMembersDepartedLast(members)}
            groupBy={memberOptionGroup}
            getOptionLabel={memberOptionLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={renderMemberOption}
            renderInput={(params) => (
              <TextField {...params} className="login-textField" size="small" />
            )}
            className="login-textField"
            size="small"
          />
        );
      },
    },
    {
      field: "referral_receiver",
      headerName: "Referral Receiver",
      flex: 1,
      minWidth: isMobileTable ? 200 : undefined,
      renderCell: (params) => (
        <MemberNameCell member={params?.row?.referral_receiver} />
      ),
      valueGetter: (params) => `${params?.first_name} ${params?.last_name}`,
      editable: true,
      renderEditCell: (params) => {
        const handleChange = (_e, e) => {
          const newValue = e;
          params.api.setEditCellValue({
            id: params.id,
            field: "referral_receiver",
            value: newValue,
          });
        };
        return (
          <Autocomplete
            value={params?.value || null}
            onChange={handleChange}
            id="referral_receiver"
            options={sortMembersDepartedLast(members)}
            groupBy={memberOptionGroup}
            getOptionLabel={memberOptionLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={renderMemberOption}
            renderInput={(params) => (
              <TextField {...params} className="login-textField" size="small" />
            )}
            className="login-textField"
            size="small"
          />
        );
      },
    },
    {
      field: "referral_name",
      headerName: "Referral Name",
      flex: 1,
      minWidth: isMobileTable ? 180 : undefined,
      editable: true,
    },
    {
      field: "referral_date",
      headerName: "Referral Date",
      flex: 1,
      minWidth: isMobileTable ? 180 : undefined,
      editable: true,
      type: "date",
      valueGetter: (params) => {
        return dayjs(params).toDate(); // returns a native JS Date
      },
      renderCell: (params) => {
        return params.value ? dayjs(params.value).format("MM/DD/YYYY") : "";
      },
      renderEditCell: (params) => {
        const handleChange = (event) => {
          const newValue = event;
          params.api.setEditCellValue({
            id: params.id,
            field: "referral_date",
            value: newValue,
          });
        };

        return (
          <DatePicker
            value={dayjs(params.value)}
            onChange={handleChange}
            format="MM/DD/YYYY"
            slotProps={{
              textField: {
                margin: "normal",
                fullWidth: true,
                id: "referral_date",
                name: "referral_date",
                className: "login-textField",
                size: "small",
              },
            }}
          />
        );
      },
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="view"
          size="small"
          onClick={() =>
            setOpenEditModal({
              open: true,
              id: params?.row?.id,
              title: params?.row?.referral_name,
            })
          }
        >
          <VisibilityIcon
            sx={{
              color: "#64748B",
            }}
          />
        </IconButton>
      ),
    },
  ];

  const fetch = async () => {
    setIsLoading(true);
    try {
      setData(await ReferralsAPI.getReferrals());
    } catch (e) {
      console.log(e.response);
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    isLoading && fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const getRowsData = () => {
    const dummyRows = [...(data?.items || [])];

    return filterArrayByString(dummyRows || [], searchText);
  };

  const processRowUpdate = async (newRow, oldRow) => {
    // Check if referral_receiver_id equals referral_sender_id
    if (newRow?.referral_receiver?.id === newRow?.referral_sender?.id) {
      openSnack(
        "Referral Sender and Referral Receiver can not be the same person.",
        "error",
      );
      return oldRow; // Revert to the old row if the IDs match
    }

    try {
      await EditReferral(newRow);
      return newRow; // Return the updated row if no issues
    } catch (error) {
      console.error("Row update failed:", error);
      return oldRow; // Revert to the old row if the update fails
    }
  };

  const EditReferral = async (form) => {
    const formObj = {
      referral_date: dayjs(form.referral_date).format("YYYY-MM-DD"),
      referral_sender_id: form.referral_sender?.id,
      referral_receiver_id: form.referral_receiver?.id,
      referral_name: form.referral_name,
    };
    try {
      await ReferralsAPI.updateReferral(formObj, form?.id);
      openSnack("Referral information updated successfully.", "success");
      fetch();
    } catch (error) {
      console.error("Error adding member:", error);
    }
  };

  return (
    <AppLayout>
      <Box
        sx={{
          width: "100%",
          padding: isMobileTable ? "8px" : "24px",
        }}
      >
        {/* Custom Header */}
        <GridCustomHeader
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          searchText={searchText}
          handleSearch={handleSearch}
          setOpenModal={setOpenModal}
          buttonText="Add New Referral"
          headerName="Referrals"
        />
        <Stack
          sx={{
            width: "100%",
            "& .MuiDataGrid-root": {
              borderRadius: "8px",
            },
          }}
          className="customDataGridFigma"
        >
          <DataGrid
            rows={getRowsData() || []}
            columns={columns}
            loading={isLoading}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
            }}
            disableVirtualization
            pageSizeOptions={[10, 15, 20, 25]}
            checkboxSelection={false}
            rowSelection={false}
            rowHeight={48}
            columnHeaderHeight={40}
            getRowClassName={(params) => {
              const customClassName =
                params.indexRelativeToCurrentPage % 2 !== 0 ? "bg-gray" : "";
              return ` ${customClassName}`.trim();
            }}
            slotProps={{
              loadingOverlay: {
                variant: "linear-progress",
                noRowsVariant: "linear-progress",
              },
            }}
            localeText={{
              noRowsLabel: "No Referrals found.",
            }}
            processRowUpdate={processRowUpdate}
          />
        </Stack>
      </Box>

      {openModal && (
        <AddReferralModal
          open={openModal}
          handleClose={() => setOpenModal(false)}
          setIsLoading={setIsLoading}
        />
      )}
      {openEditModal.open && (
        <EditReferralModal
          open={openEditModal?.open}
          id={openEditModal?.id}
          handleClose={() => setOpenEditModal(defaultEditFormState)}
          setIsLoading={setIsLoading}
          title={openEditModal?.title}
        />
      )}
    </AppLayout>
  );
};

export default Referral;
